import { useRef, useState, useEffect, useCallback } from "react";
import axios from "axios";

import {
  getWeekStart,
  getWeekEnd,
  getWeekDays,
  shiftWeek,
  formatDateNL,
  getWeekYearForDate
} from "../../utils/dateUtils.js";
import { getISOWeek } from "date-fns";
import { useAuth } from '../../context/AuthContext.js';
import HourValue from './HourValue';

const UserHours = () => {
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart());
  const [tasks, setTasks] = useState([]);
  const [hours, setHours] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expandedCards, setExpandedCards] = useState({});
  const [weekStatus, setWeekStatus] = useState('draft');

  const daysOfWeek = getWeekDays(currentWeekStart);
  const weekNumber = getISOWeek(currentWeekStart);
  const weekYear = getWeekYearForDate(currentWeekStart);

  const token = sessionStorage.getItem('jwtToken');
  const { userInfo } = useAuth();
  const [errorMessage, setErrorMessage] = useState("");
  const [errorVisible, setErrorVisible] = useState(false);
  const errorTimeoutRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showError = (message) => {
    setErrorMessage(message);
    setErrorVisible(true);

    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    errorTimeoutRef.current = setTimeout(() => {
      setErrorVisible(false);
      setErrorMessage("");
    }, 5000);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const deleteUserHour = async (userHoursId) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/user-hours/delete/${userHoursId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (error) {
      console.error('Fout bij verwijderen record:', error);
      return false;
    }
  };

  useEffect(() => {
    setHours({});
    setExpandedCards({});
    setWeekStatus('draft');
  }, [currentWeekStart, weekNumber]);

  const fetchHours = useCallback(async () => {
    if (!userInfo?.user_id) return;

    try {
      const currentDaysOfWeek = getWeekDays(currentWeekStart);

      const [hoursRes, tasksRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/user-hours/myhours`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            year: weekYear,
            week: weekNumber,
          },
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/tasks/mytasks/${userInfo.user_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const hourEntries = hoursRes.data.data || [];
      const taskEntries = tasksRes.data.Data || [];

      setTasks(taskEntries);

      const newHours = {};

      hourEntries.forEach((hourEntry) => {
        const taskHours = {};
        let hasNonZeroHours = false;

        currentDaysOfWeek.forEach((day, i) => {
          const dateKey = formatDateNL(day, "yyyy-MM-dd");
          const dayKey = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"][i];
          const value = parseFloat(hourEntry[dayKey]) || 0;
          taskHours[dateKey] = value;

          if (value > 0) {
            hasNonZeroHours = true;
          }
        });

        if (hasNonZeroHours) {
          newHours[hourEntry.task_id] = {
            user_hour_id: hourEntry.user_hour_id,
            hours: taskHours
          };
        }
      });

      if (hourEntries.length > 0) {
        setWeekStatus(hourEntries[0].status || 'draft');
      } else {
        setWeekStatus('draft');
      }

      setHours(newHours);
    } catch (err) {
      console.error("Fout bij ophalen taken of uren:", err);
    }
  }, [currentWeekStart, token, userInfo?.user_id, weekNumber, weekYear]);

  useEffect(() => {
    if (!userInfo?.user_id) return;
    fetchHours();
  }, [userInfo?.user_id, fetchHours]);

  const handleHourChange = (taskId, date, value) => {
    if (!isEditable()) return;

    setHours((prev) => {
      const taskEntry = prev[taskId] || { user_hour_id: null, hours: {} };
      return {
        ...prev,
        [taskId]: {
          ...taskEntry,
          hours: {
            ...taskEntry.hours,
            [date]: value === "" ? "" : Number(value) || 0,
          },
        },
      };
    });
  };

  const getTaskTotal = (taskId) => {
    const taskEntry = hours[taskId];
    if (!taskEntry || !taskEntry.hours) return 0;
    return Object.values(taskEntry.hours).reduce((sum, val) => {
      if (val === "") return sum;
      return sum + Number(val);
    }, 0);
  };

  const getWeekTotal = () =>
    Object.keys(hours).reduce((total, taskId) => total + getTaskTotal(taskId), 0);

  const isEditable = () => {
    return !weekStatus || !['ingediend', 'goedgekeurd'].includes(weekStatus);
  };

  const saveAllHours = async () => {
    let success = true;

    for (const taskId of Object.keys(hours)) {
      const taskEntry = hours[taskId];
      const task = tasks.find((t) => t.task_id === taskId);
      if (!task) continue;

      const taskHours = taskEntry.hours || {};
      const hasHours = Object.values(taskHours).some((h) => h > 0);

      if (hasHours) {
        const payload = {
          user_id: userInfo.user_id,
          task_id: taskId,
          week_year: weekYear,
          week_number: weekNumber,
          monday: taskHours[formatDateNL(daysOfWeek[0], "yyyy-MM-dd")] || 0,
          tuesday: taskHours[formatDateNL(daysOfWeek[1], "yyyy-MM-dd")] || 0,
          wednesday: taskHours[formatDateNL(daysOfWeek[2], "yyyy-MM-dd")] || 0,
          thursday: taskHours[formatDateNL(daysOfWeek[3], "yyyy-MM-dd")] || 0,
          friday: taskHours[formatDateNL(daysOfWeek[4], "yyyy-MM-dd")] || 0,
          saturday: taskHours[formatDateNL(daysOfWeek[5], "yyyy-MM-dd")] || 0,
          sunday: taskHours[formatDateNL(daysOfWeek[6], "yyyy-MM-dd")] || 0,
        };

        try {
          const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/user-hours/upserthours`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (res.data.status === "error") {
            success = false;
            console.error(`Fout bij opslaan uren voor taak ${task.name}`, res.data.message);
          }
        } catch (err) {
          success = false;
          console.error(`Fout bij opslaan uren voor taak ${task.name}`, err);
        }
      }
    }

    return success;
  };

  const updateWeekStatus = async (status) => {
    try {
      const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/user-hours/updateweekstatus`, {
        user_id: userInfo.user_id,
        week_year: weekYear,
        week_number: weekNumber,
        status
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.status === "error") {
        throw new Error(res.data.message || "Technische fout bij status-update");
      }

      return true;
    } catch (err) {
      console.error("Fout bij updaten weekstatus:", err);
      return false;
    }
  };

  const saveHoursWithStatus = async (status = "draft") => {
    setErrorMessage("");
    setIsSubmitting(true);

    const saved = await saveAllHours();

    if (!saved) {
      showError("Er is een technische fout opgetreden bij het opslaan van uren. Probeer opnieuw.");
      setIsSubmitting(false);
      return;
    }

    if (status !== "draft") {
      const updated = await updateWeekStatus(status);

      if (!updated) {
        showError("Er is een technische fout opgetreden bij het indienen. Probeer opnieuw.");
        setIsSubmitting(false);
        return;
      }
    }

    alert(`Uren zijn ${status === "ingediend" ? "ingediend" : "opgeslagen als concept"}!`);
    setWeekStatus(status);
    await fetchHours();
    setIsSubmitting(false);
  };

  const toggleExpanded = (taskId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const MobileTaskCard = ({ taskId, task, taskEntry, taskHours }) => {
    const isNewRow = taskId.startsWith('new-');
    const isExpanded = isNewRow || expandedCards[taskId];
    const editable = isEditable();

    return (
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="flex-grow-1">
            {isNewRow ? (
              <select
                className="form-select form-select-sm"
                value={task.task_id ? taskId : ""}
                onChange={(e) => {
                  const newTaskId = e.target.value;
                  if (newTaskId === "") {
                    setHours((prev) => {
                      const newHours = { ...prev };
                      delete newHours[taskId];
                      return newHours;
                    });
                    setExpandedCards((prev) => {
                      const newExpanded = { ...prev };
                      delete newExpanded[taskId];
                      return newExpanded;
                    });
                  } else {
                    setHours((prev) => {
                      const newHours = { ...prev };
                      const existingHours = newHours[taskId];
                      delete newHours[taskId];
                      newHours[newTaskId] = existingHours;
                      return newHours;
                    });
                    setExpandedCards((prev) => {
                      const newExpanded = { ...prev };
                      newExpanded[newTaskId] = true;
                      delete newExpanded[taskId];
                      return newExpanded;
                    });
                  }
                }}
                disabled={!editable}
              >
                <option value="">-- Kies taak --</option>
                {tasks.map((t) => (
                  <option
                    key={t.task_id}
                    value={t.task_id}
                    disabled={Object.keys(hours).includes(t.task_id) && t.task_id !== taskId}
                  >
                    {t.name}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <h6 className="mb-0">{task.name || "Onbekende taak"}</h6>
                <small className="text-muted">Totaal: <HourValue value={getTaskTotal(taskId)} /> uur</small>
              </>
            )}
          </div>
          <div className="d-flex gap-2">
            {editable && (
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={async () => {
                  const isInDatabase = taskEntry?.user_hour_id != null;
                  const message = isInDatabase
                    ? "Weet je zeker dat je deze rij wilt verwijderen? Dit verwijdert het ook uit de database."
                    : "Weet je zeker dat je deze rij wilt verwijderen?";

                  if (window.confirm(message)) {
                    if (isInDatabase) {
                      const success = await deleteUserHour(taskEntry.user_hour_id);
                      if (!success) {
                        alert('Fout bij verwijderen uit database');
                        return;
                      }
                    }
                    setHours((prev) => {
                      const newHours = { ...prev };
                      delete newHours[taskId];
                      return newHours;
                    });
                    setExpandedCards((prev) => {
                      const newExpanded = { ...prev };
                      delete newExpanded[taskId];
                      return newExpanded;
                    });
                  }
                }}
                title="Verwijderen"
              >
                ×
              </button>
            )}
            {!isNewRow && (
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => toggleExpanded(taskId)}
              >
                {isExpanded ? "↑" : "↓"}
              </button>
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="card-body">
            <div className="row g-2">
              {daysOfWeek.map((day) => {
                const dateKey = formatDateNL(day, "yyyy-MM-dd");
                const dayName = formatDateNL(day, "EEE");
                const dayDate = formatDateNL(day, "dd/MM");

                return (
                  <div key={dateKey} className="col-6">
                    <label className="form-label small mb-1">
                      {dayName} {dayDate}
                    </label>
                    <input
                      type="number"
                      className={`form-control form-control-sm ${!editable ? 'bg-light' : ''}`}
                      min="0"
                      step="0.25"
                      value={taskHours[dateKey] ?? ""}
                      onChange={(e) => handleHourChange(taskId, dateKey, e.target.value)}
                      disabled={!editable || (isNewRow && !task.task_id)}
                      readOnly={!editable}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container-fluid px-2 px-md-3 px-lg-5 mt-3">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3 gap-2">
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setCurrentWeekStart(shiftWeek(currentWeekStart, -1))}
        >
          ← Vorige
        </button>
        <h6 className="my-0 text-center flex-grow-1 mx-2">
          Week {weekNumber}<br />
          <small className="text-muted">
            {formatDateNL(currentWeekStart, "dd MMM")} - {formatDateNL(getWeekEnd(currentWeekStart), "dd MMM")}
          </small>
        </h6>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setCurrentWeekStart(shiftWeek(currentWeekStart, 1))}
        >
          Volgende →
        </button>
      </div>

      <div className={`alert ${weekStatus === 'goedgekeurd' ? 'alert-success' :
        weekStatus === 'ingediend' ? 'alert-warning' : 'alert-info'
        } mb-3`}>
        <strong>Week status:</strong>
        <span className={`badge ms-2 ${weekStatus === 'goedgekeurd' ? 'bg-success' :
          weekStatus === 'ingediend' ? 'bg-warning' : 'bg-secondary'
          }`}>
          {weekStatus}
        </span>
        {weekStatus === 'ingediend' || weekStatus === 'goedgekeurd' ? (
          <span className="ms-2">(Deze week kan niet meer worden bewerkt)</span>
        ) : null}
      </div>

      {isMobile ? (
        <div className="mobile-hours-view">
          <div className="mb-3">
            <p className="text-muted small text-center">
              {Object.keys(hours).length > 0 ? "Tik op ↓ om uren per dag in te voeren" : "Voeg een taak toe om uren in te voeren"}
            </p>
          </div>

          {Object.keys(hours).length === 0 && tasks.length > 0 ? (
            <div className="alert alert-info text-center">
              Geen uren ingevoerd voor deze week
            </div>
          ) : Object.keys(hours).length === 0 && tasks.length === 0 ? (
            <div className="alert alert-warning text-center">
              Geen taken beschikbaar
            </div>
          ) : (
            Object.keys(hours).map((taskId) => {
              const taskEntry = hours[taskId];
              const task = tasks.find((t) => t.task_id === taskId) || {};
              const taskHours = taskEntry?.hours || {};

              return (
                <MobileTaskCard
                  key={taskId}
                  taskId={taskId}
                  task={task}
                  taskEntry={taskEntry}
                  taskHours={taskHours}
                />
              );
            })
          )}

          {Object.keys(hours).length > 0 && (
            <div className="card bg-light">
              <div className="card-body text-center py-2">
                <strong>Week totaal: <HourValue value={getWeekTotal()} /> uur</strong>
              </div>
            </div>
          )}

          <div className="mt-3 d-flex flex-column gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                const newTaskId = "new-" + Date.now();
                setHours((prev) => ({
                  ...prev,
                  [newTaskId]: {
                    user_hour_id: null,
                    hours: daysOfWeek.reduce((acc, day) => {
                      acc[formatDateNL(day, "yyyy-MM-dd")] = 0;
                      return acc;
                    }, {})
                  }
                }));
                setExpandedCards((prev) => ({
                  ...prev,
                  [newTaskId]: true
                }));
              }}
              disabled={!isEditable()}
            >
              + Nieuwe taak toevoegen
            </button>

            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary flex-fill"
                onClick={() => saveHoursWithStatus("draft")}
                disabled={!isEditable()}
              >
                Opslaan
              </button>
              <button
                className="btn btn-success flex-fill"
                onClick={() => saveHoursWithStatus("ingediend")}
                disabled={!isEditable()}
              >
                Indienen
              </button>
              {errorVisible && (
                <div
                  className="position-fixed bottom-0 start-50 translate-middle-x mb-4 px-4 py-2 bg-danger text-white rounded shadow d-flex align-items-center justify-content-between"
                  style={{
                    bottom: "80px",
                    minWidth: "90vw",
                    maxWidth: "600px",
                    zIndex: 1055,
                    fontSize: "0.95rem",
                    flexWrap: "wrap"
                  }}
                >
                  <div className="d-flex align-items-center flex-grow-1">
                    <strong className="me-2">Fout:</strong> {errorMessage}
                    {isSubmitting && (
                      <div className="spinner-border spinner-border-sm text-light ms-3" role="status" />
                    )}
                  </div>
                  <button
                    className="btn btn-sm btn-light ms-3"
                    onClick={() => {
                      setErrorVisible(false);
                      setErrorMessage("");
                      clearTimeout(errorTimeoutRef.current);
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-3">
            <p className="text-muted">Selecteer voor elke rij een taak om uren in te voeren</p>
          </div>

          <div
            className="table-responsive"
            style={{ maxHeight: "500px", overflowY: "auto" }}
          >
            <table className="table table-bordered table-sm">
              <thead className="table-light">
                <tr>
                  <th
                    style={{
                      position: "sticky",
                      left: 0,
                      top: 0,
                      backgroundColor: "#f8f9fa",
                      zIndex: 3,
                      minWidth: "150px",
                    }}
                  >
                    Taak
                  </th>
                  {daysOfWeek.map((day) => (
                    <th
                      key={day}
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f8f9fa",
                        zIndex: 2,
                        minWidth: "100px",
                      }}
                    >
                      {formatDateNL(day, "EEEEEE dd/MM")}
                    </th>
                  ))}
                  <th
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#f8f9fa",
                      zIndex: 2,
                      minWidth: "80px",
                    }}
                  >
                    Totaal
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(hours).length === 0 && tasks.length > 0 ? (
                  <tr>
                    <td colSpan={daysOfWeek.length + 2} className="text-center">
                      Geen uren ingevoerd voor deze week
                    </td>
                  </tr>
                ) : Object.keys(hours).length === 0 && tasks.length === 0 ? (
                  <tr>
                    <td colSpan={daysOfWeek.length + 2} className="text-center">
                      Geen taken beschikbaar
                    </td>
                  </tr>
                ) : (
                  Object.keys(hours).map((taskId) => {
                    const taskEntry = hours[taskId];
                    const task = tasks.find((t) => t.task_id === taskId) || {};
                    const taskHours = taskEntry?.hours || {};
                    const isNewRow = taskId.startsWith('new-');
                    const editable = isEditable();

                    return (
                      <tr key={taskId}>
                        <td style={{ position: "sticky", left: 0, backgroundColor: "#fff", zIndex: 1, minWidth: "150px" }}>
                          {isNewRow ? (
                            <select
                              className="form-select form-select-sm"
                              value={task.task_id ? taskId : ""}
                              onChange={(e) => {
                                const newTaskId = e.target.value;
                                if (newTaskId === "") {
                                  setHours((prev) => {
                                    const newHours = { ...prev };
                                    delete newHours[taskId];
                                    return newHours;
                                  });
                                } else {
                                  setHours((prev) => {
                                    const newHours = { ...prev };
                                    const existingHours = newHours[taskId];
                                    delete newHours[taskId];
                                    newHours[newTaskId] = existingHours;
                                    return newHours;
                                  });
                                }
                              }}
                              disabled={!editable}
                            >
                              <option value="">-- Kies taak --</option>
                              {tasks.map((t) => (
                                <option
                                  key={t.task_id}
                                  value={t.task_id}
                                  disabled={Object.keys(hours).includes(t.task_id) && t.task_id !== taskId}
                                >
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="d-flex align-items-center gap-2">
                              <span className="flex-grow-1">{task.name || "Onbekende taak"}</span>
                              {editable && (
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={async () => {
                                    const isInDatabase = taskEntry?.user_hour_id != null;
                                    const message = isInDatabase
                                      ? "Weet je zeker dat je deze rij wilt verwijderen? Dit verwijdert het ook uit de database."
                                      : "Weet je zeker dat je deze rij wilt verwijderen?";

                                    if (window.confirm(message)) {
                                      if (isInDatabase) {
                                        const success = await deleteUserHour(taskEntry.user_hour_id);
                                        if (!success) {
                                          alert('Fout bij verwijderen uit database');
                                          return;
                                        }
                                      }
                                      setHours((prev) => {
                                        const newHours = { ...prev };
                                        delete newHours[taskId];
                                        return newHours;
                                      });
                                    }
                                  }}
                                  title="Verwijderen"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        {daysOfWeek.map((day) => {
                          const dateKey = formatDateNL(day, "yyyy-MM-dd");
                          return (
                            <td key={dateKey}>
                              <input
                                type="number"
                                className={`form-control form-control-sm ${!editable ? 'bg-light' : ''}`}
                                min="0"
                                step="0.25"
                                value={taskHours[dateKey] ?? ""}
                                onChange={(e) => handleHourChange(taskId, dateKey, e.target.value)}
                                disabled={!editable || (isNewRow && !task.task_id)}
                                readOnly={!editable}
                              />
                            </td>
                          );
                        })}
                        <td>
                          <strong><HourValue value={getTaskTotal(taskId)} /></strong>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              {Object.keys(hours).length > 0 && (
                <tfoot>
                  <tr className="table-light">
                    <td colSpan={daysOfWeek.length + 1} className="text-end">
                      <strong>Week totaal:</strong>
                    </td>
                    <td>
                      <strong><HourValue value={getWeekTotal()} /></strong>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="mt-3 d-flex justify-content-between align-items-center">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setHours((prev) => ({
                  ...prev,
                  ["new-" + Date.now()]: {
                    user_hour_id: null,
                    hours: daysOfWeek.reduce((acc, day) => {
                      acc[formatDateNL(day, "yyyy-MM-dd")] = 0;
                      return acc;
                    }, {})
                  }
                }));
              }}
              disabled={!isEditable()}
            >
              + Nieuwe rij toevoegen
            </button>

            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary"
                onClick={() => saveHoursWithStatus("draft")}
                disabled={!isEditable()}
              >
                Opslaan als concept
              </button>
              <button
                className="btn btn-success"
                onClick={() => saveHoursWithStatus("ingediend")}
                disabled={!isEditable()}
              >
                Indienen
              </button>
            </div>

            {errorVisible && (
              <div
                className="position-fixed bottom-0 start-50 translate-middle-x mb-4 px-4 py-2 bg-danger text-white rounded shadow d-flex align-items-center justify-content-between"
                style={{ minWidth: "300px", zIndex: 9999 }}
              >
                <div className="d-flex align-items-center">
                  <strong className="me-2">Fout:</strong> {errorMessage}
                </div>
                <button
                  className="btn btn-sm btn-light ms-3"
                  onClick={() => {
                    setErrorVisible(false);
                    setErrorMessage("");
                    clearTimeout(errorTimeoutRef.current);
                  }}
                >
                  ×
                </button>
              </div>
            )}

            {isSubmitting && <div className="spinner-border text-primary ms-2" role="status" />}
          </div>
        </>
      )}
    </div>
  );
};

export default UserHours;
